import { type AppResponse } from '@autone/utils';

import { coreApiNext, coreQueryBuilder } from './core-api';
import {
  type TransformedGetCoreCatalogueEventsResponse,
  type TransformedGetCoreCatalogueSeasonsResponse,
  type TransformedGetInfiniteCoreCatalogueEventsRequest,
  type TransformedGetInfiniteCoreCatalogueSeasonsRequest,
} from './core-catalogue-filters';
import {
  type TransformedGetCoreLocationClustersResponse,
  type TransformedGetInfiniteCoreLocationClustersRequest,
} from './core-location-clusters-filters';
import {
  type TransformedGetCoreLocationCitiesResponse,
  type TransformedGetCoreLocationCountriesResponse,
  type TransformedGetCoreLocationRegionsResponse,
  type TransformedGetCoreLocationResponse,
  type TransformedGetCoreLocationTerritoriesResponse,
  type TransformedGetCoreLocationTypesResponse,
  type TransformedGetInfiniteCoreLocationCitiesRequest,
  type TransformedGetInfiniteCoreLocationCountriesRequest,
  type TransformedGetInfiniteCoreLocationRegionsRequest,
  type TransformedGetInfiniteCoreLocationRequest,
  type TransformedGetInfiniteCoreLocationTerritoriesRequest,
  type TransformedGetInfiniteCoreLocationTypesRequest,
} from './core-location-filters';
import {
  type TransformedGetCoreLocationGroupsResponse,
  type TransformedGetInfiniteCoreLocationGroupsRequest,
} from './core-location-groups-filters';
import {
  type TransformedGetCoreProductArticlesResponse,
  type TransformedGetCoreProductBrandsResponse,
  type TransformedGetCoreProductBudgetLevelsResponse,
  type TransformedGetCoreProductClassesResponse,
  type TransformedGetCoreProductCollectionTypesResponse,
  type TransformedGetCoreProductColorsResponse,
  type TransformedGetCoreProductDepartmentsResponse,
  type TransformedGetCoreProductEventsResponse,
  type TransformedGetCoreProductGendersResponse,
  type TransformedGetCoreProductManufacturersResponse,
  type TransformedGetCoreProductMaterialsResponse,
  type TransformedGetCoreProductResponse,
  type TransformedGetCoreProductSeasonEventsResponse,
  type TransformedGetCoreProductSeasonsResponse,
  type TransformedGetCoreProductStylesResponse,
  type TransformedGetCoreProductSubClassesResponse,
  type TransformedGetCoreProductSubDepartmentsResponse,
  type TransformedGetInfiniteCoreProductArticlesRequest,
  type TransformedGetInfiniteCoreProductBrandsRequest,
  type TransformedGetInfiniteCoreProductBudgetLevelsRequest,
  type TransformedGetInfiniteCoreProductClassesRequest,
  type TransformedGetInfiniteCoreProductCollectionTypesRequest,
  type TransformedGetInfiniteCoreProductColorsRequest,
  type TransformedGetInfiniteCoreProductDepartmentsRequest,
  type TransformedGetInfiniteCoreProductEventsRequest,
  type TransformedGetInfiniteCoreProductGendersRequest,
  type TransformedGetInfiniteCoreProductManufacturersRequest,
  type TransformedGetInfiniteCoreProductMaterialsRequest,
  type TransformedGetInfiniteCoreProductRequest,
  type TransformedGetInfiniteCoreProductSeasonEventsRequest,
  type TransformedGetInfiniteCoreProductSeasonsRequest,
  type TransformedGetInfiniteCoreProductStylesRequest,
  type TransformedGetInfiniteCoreProductSubClassesRequest,
  type TransformedGetInfiniteCoreProductSubDepartmentsRequest,
} from './core-product-filters';
import {
  type TransformedGetCoreProductGroupsResponse,
  type TransformedGetInfiniteCoreProductGroupsRequest,
} from './core-product-groups-filters';
import {
  type TransformedGetCoreSizesResponse,
  type TransformedGetInfiniteCoreSizesRequest,
} from './core-size-filters';
import {
  type TransformedGetCoreSizeRunsResponse,
  type TransformedGetInfiniteCoreSizeRunsRequest,
} from './core-size-run-filters';
import {
  type TransformedGetCoreSkusResponse,
  type TransformedGetInfiniteCoreSkusRequest,
} from './core-sku-filters';
import {
  type FrontendFilterOption,
  type GetPathResponseItem,
} from './transformers/create-transformers.types';
import {
  requestTransformers,
  responseTransformers,
} from './transformers/transformers';

type PageParam = {
  cursor: string | undefined;
};

const coreApiNextFilters = coreApiNext.injectEndpoints({
  endpoints: (builder) => ({
    // Infinite Query Product endpoints
    getCoreProducts: builder.infiniteQuery<
      TransformedGetCoreProductResponse,
      TransformedGetInfiniteCoreProductRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products',
          requestTransformers['/core/products'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products'],
    }),
    /**
     * Single-shot lookup of `/core/products` by a list of product IDs. The
     * response is mapped through the standard `/core/products` response
     * transformer so consumers receive ready-to-use `FrontendFilterOption`
     * items instead of having to stub one out from raw product IDs.
     */
    getCoreProductsByIds: builder.query<
      FrontendFilterOption<
        '/core/products',
        GetPathResponseItem<'/core/products'>
      >[],
      { productIds: string[] }
    >({
      query: ({ productIds }) => {
        const queryFn = coreQueryBuilder('post', '/products');
        return queryFn({
          body: {
            filters: {
              product: { products: productIds.map((id) => ({ id })) },
              catalogue: {},
              product_groups: {},
              location_cluster: {},
              size_run: {},
              size: {},
            },
          },
          query: { limit: productIds.length },
        });
      },
      transformResponse: (
        response: AppResponse<'core', 'post', '/core/products', 200>,
      ) => responseTransformers['/core/products'](response).items,
    }),
    getCoreProductDepartments: builder.infiniteQuery<
      TransformedGetCoreProductDepartmentsResponse,
      TransformedGetInfiniteCoreProductDepartmentsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/departments',
          requestTransformers['/core/products/departments'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/departments'],
    }),
    getCoreProductSubDepartments: builder.infiniteQuery<
      TransformedGetCoreProductSubDepartmentsResponse,
      TransformedGetInfiniteCoreProductSubDepartmentsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/sub-departments',
          requestTransformers['/core/products/sub-departments'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/sub-departments'],
    }),
    getCoreProductClasses: builder.infiniteQuery<
      TransformedGetCoreProductClassesResponse,
      TransformedGetInfiniteCoreProductClassesRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/classes',
          requestTransformers['/core/products/classes'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/classes'],
    }),
    getCoreProductSubClasses: builder.infiniteQuery<
      TransformedGetCoreProductSubClassesResponse,
      TransformedGetInfiniteCoreProductSubClassesRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/sub-classes',
          requestTransformers['/core/products/sub-classes'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/sub-classes'],
    }),
    getCoreProductManufacturers: builder.infiniteQuery<
      TransformedGetCoreProductManufacturersResponse,
      TransformedGetInfiniteCoreProductManufacturersRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/manufacturers',
          requestTransformers['/core/products/manufacturers'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/manufacturers'],
    }),
    getCoreProductBrands: builder.infiniteQuery<
      TransformedGetCoreProductBrandsResponse,
      TransformedGetInfiniteCoreProductBrandsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/brands',
          requestTransformers['/core/products/brands'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/brands'],
    }),
    getCoreProductCollectionTypes: builder.infiniteQuery<
      TransformedGetCoreProductCollectionTypesResponse,
      TransformedGetInfiniteCoreProductCollectionTypesRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/collection-types',
          requestTransformers['/core/products/collection-types'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse:
        responseTransformers['/core/products/collection-types'],
    }),
    getCoreProductSeasons: builder.infiniteQuery<
      TransformedGetCoreProductSeasonsResponse,
      TransformedGetInfiniteCoreProductSeasonsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/seasons',
          requestTransformers['/core/products/seasons'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/seasons'],
    }),
    getCoreProductEvents: builder.infiniteQuery<
      TransformedGetCoreProductEventsResponse,
      TransformedGetInfiniteCoreProductEventsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/events',
          requestTransformers['/core/products/events'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/events'],
    }),
    getCoreProductSeasonsEvents: builder.infiniteQuery<
      TransformedGetCoreProductSeasonEventsResponse,
      TransformedGetInfiniteCoreProductSeasonEventsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/season-events',
          requestTransformers['/core/products/season-events'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/season-events'],
    }),
    getCoreProductArticles: builder.infiniteQuery<
      TransformedGetCoreProductArticlesResponse,
      TransformedGetInfiniteCoreProductArticlesRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/articles',
          requestTransformers['/core/products/articles'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/articles'],
    }),
    getCoreProductBudgetLevels: builder.infiniteQuery<
      TransformedGetCoreProductBudgetLevelsResponse,
      TransformedGetInfiniteCoreProductBudgetLevelsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/budget-levels',
          requestTransformers['/core/products/budget-levels'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/budget-levels'],
    }),
    getCoreProductMaterials: builder.infiniteQuery<
      TransformedGetCoreProductMaterialsResponse,
      TransformedGetInfiniteCoreProductMaterialsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/materials',
          requestTransformers['/core/products/materials'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/materials'],
    }),
    getCoreProductStyles: builder.infiniteQuery<
      TransformedGetCoreProductStylesResponse,
      TransformedGetInfiniteCoreProductStylesRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/styles',
          requestTransformers['/core/products/styles'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/styles'],
    }),
    getCoreProductColors: builder.infiniteQuery<
      TransformedGetCoreProductColorsResponse,
      TransformedGetInfiniteCoreProductColorsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/colors',
          requestTransformers['/core/products/colors'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/colors'],
    }),
    getCoreProductGenders: builder.infiniteQuery<
      TransformedGetCoreProductGendersResponse,
      TransformedGetInfiniteCoreProductGendersRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/products/genders',
          requestTransformers['/core/products/genders'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/products/genders'],
    }),

    // Infinite Query Location endpoints
    getCoreLocations: builder.infiniteQuery<
      TransformedGetCoreLocationResponse,
      TransformedGetInfiniteCoreLocationRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/locations',
          requestTransformers['/core/locations'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/locations'],
    }),
    getCoreLocationTypes: builder.infiniteQuery<
      TransformedGetCoreLocationTypesResponse,
      TransformedGetInfiniteCoreLocationTypesRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/locations/location-types',
          requestTransformers['/core/locations/location-types'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/locations/location-types'],
    }),
    getCoreLocationTerritories: builder.infiniteQuery<
      TransformedGetCoreLocationTerritoriesResponse,
      TransformedGetInfiniteCoreLocationTerritoriesRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/locations/territories',
          requestTransformers['/core/locations/territories'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/locations/territories'],
    }),
    getCoreLocationRegions: builder.infiniteQuery<
      TransformedGetCoreLocationRegionsResponse,
      TransformedGetInfiniteCoreLocationRegionsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/locations/regions',
          requestTransformers['/core/locations/regions'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/locations/regions'],
    }),
    getCoreLocationCountries: builder.infiniteQuery<
      TransformedGetCoreLocationCountriesResponse,
      TransformedGetInfiniteCoreLocationCountriesRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/locations/countries',
          requestTransformers['/core/locations/countries'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/locations/countries'],
    }),
    getCoreLocationCities: builder.infiniteQuery<
      TransformedGetCoreLocationCitiesResponse,
      TransformedGetInfiniteCoreLocationCitiesRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/locations/cities',
          requestTransformers['/core/locations/cities'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/locations/cities'],
    }),

    // Infinite Query Catalogue endpoints
    getCoreCatalogueSeasons: builder.infiniteQuery<
      TransformedGetCoreCatalogueSeasonsResponse,
      TransformedGetInfiniteCoreCatalogueSeasonsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/catalogue/seasons',
          requestTransformers['/core/catalogue/seasons'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/catalogue/seasons'],
    }),
    getCoreCatalogueEvents: builder.infiniteQuery<
      TransformedGetCoreCatalogueEventsResponse,
      TransformedGetInfiniteCoreCatalogueEventsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/catalogue/events',
          requestTransformers['/core/catalogue/events'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/catalogue/events'],
    }),

    // Infinite Query Product Group endpoints
    getCoreProductGroups: builder.infiniteQuery<
      TransformedGetCoreProductGroupsResponse,
      TransformedGetInfiniteCoreProductGroupsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/product-groups',
          requestTransformers['/core/product-groups'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/product-groups'],
    }),

    // Infinite Query Location Cluster endpoints
    getCoreLocationClusters: builder.infiniteQuery<
      TransformedGetCoreLocationClustersResponse,
      TransformedGetInfiniteCoreLocationClustersRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/location-clusters',
          requestTransformers['/core/location-clusters'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/location-clusters'],
    }),

    // Infinite Query Location Group endpoints
    getCoreLocationGroups: builder.infiniteQuery<
      TransformedGetCoreLocationGroupsResponse,
      TransformedGetInfiniteCoreLocationGroupsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/location-groups',
          requestTransformers['/core/location-groups'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/location-groups'],
    }),

    // Infinite Query Sku endpoints
    getCoreSkus: builder.infiniteQuery<
      TransformedGetCoreSkusResponse,
      TransformedGetInfiniteCoreSkusRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/skus',
          requestTransformers['/core/skus'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/skus'],
    }),

    // Infinite Query Size endpoints
    getCoreSizes: builder.infiniteQuery<
      TransformedGetCoreSizesResponse,
      TransformedGetInfiniteCoreSizesRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/sizes',
          requestTransformers['/core/sizes'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/sizes'],
    }),

    // Infinite Query Size Run endpoints
    getCoreSizeRuns: builder.infiniteQuery<
      TransformedGetCoreSizeRunsResponse,
      TransformedGetInfiniteCoreSizeRunsRequest,
      PageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = coreQueryBuilder(
          'post',
          '/size-runs',
          requestTransformers['/core/size-runs'],
        );
        return queryFn({ body, query: { ...query, cursor } });
      },
      transformResponse: responseTransformers['/core/size-runs'],
    }),
  }),
});

export const {
  // Query Product hooks
  useGetCoreProductsByIdsQuery,
  useLazyGetCoreProductsByIdsQuery,
  // Infinite Query Product hooks
  useGetCoreProductsInfiniteQuery,
  useGetCoreProductDepartmentsInfiniteQuery,
  useGetCoreProductSubDepartmentsInfiniteQuery,
  useGetCoreProductClassesInfiniteQuery,
  useGetCoreProductSubClassesInfiniteQuery,
  useGetCoreProductManufacturersInfiniteQuery,
  useGetCoreProductBrandsInfiniteQuery,
  useGetCoreProductCollectionTypesInfiniteQuery,
  useGetCoreProductSeasonsInfiniteQuery,
  useGetCoreProductEventsInfiniteQuery,
  useGetCoreProductSeasonsEventsInfiniteQuery,
  useGetCoreProductArticlesInfiniteQuery,
  useGetCoreProductBudgetLevelsInfiniteQuery,
  useGetCoreProductMaterialsInfiniteQuery,
  useGetCoreProductStylesInfiniteQuery,
  useGetCoreProductColorsInfiniteQuery,
  useGetCoreProductGendersInfiniteQuery,
  // Infinite Query Location hooks
  useGetCoreLocationsInfiniteQuery,
  useGetCoreLocationTypesInfiniteQuery,
  useGetCoreLocationTerritoriesInfiniteQuery,
  useGetCoreLocationRegionsInfiniteQuery,
  useGetCoreLocationCountriesInfiniteQuery,
  useGetCoreLocationCitiesInfiniteQuery,
  // Infinite Query Catalogue hooks
  useGetCoreCatalogueSeasonsInfiniteQuery,
  useGetCoreCatalogueEventsInfiniteQuery,
  // Infinite Query Product Group hooks
  useGetCoreProductGroupsInfiniteQuery,
  // Infinite Query Location Cluster hooks
  useGetCoreLocationClustersInfiniteQuery,
  // Infinite Query Location Group hooks
  useGetCoreLocationGroupsInfiniteQuery,
  // Infinite Query Sku hooks
  useGetCoreSkusInfiniteQuery,
  // Infinite Query Size hooks
  useGetCoreSizesInfiniteQuery,
  // Infinite Query Size Run hooks
  useGetCoreSizeRunsInfiniteQuery,
} = coreApiNextFilters;
